// Reactive project store. Holds the project data + decoded audio buffers,
// emits change events, and provides mutation helpers with basic undo/redo.
import { Emitter, uid } from "../util";
import { AudioAsset, MediaAsset, Clip, Effect, Project, Track, TrackKind, TRACK_PREFIX, newTrack, newProject, clipSpeed, Marker } from "./model";
import { clipBefore, clipAfter, DEFAULT_TRANSITION } from "./transitions";
import type { GeneratorSpec } from "./model";
import { VisualSpec, GENERATOR_LABELS, generatorDurations, synthAudio } from "../video/generators";

/** Clipboard entry: a copied clip plus the track it came from, positioned relative
 *  to the earliest copied clip. */
interface ClipboardItem { trackId: string; trackKind: TrackKind; rel: number; clip: Clip; }
const EPS = 1e-6;
import { VideoSource } from "../video/source";

type ChangeScope = "project" | "tracks" | "transport" | "selection" | "assets" | "mixer";

interface StoreEvents {
  change: { scope: ChangeScope };
  selection: { trackId: string | null; clipId: string | null };
}

export class Store extends Emitter<StoreEvents> {
  project: Project;
  /** assetId -> decoded buffer (kept out of undo snapshots) */
  buffers = new Map<string, AudioBuffer>();
  /** assetId -> video source element (kept out of undo snapshots, like buffers) */
  videos = new Map<string, VideoSource>();
  selectedTrackId: string | null = null;
  selectedClipId: string | null = null; // "primary" clip (last clicked)
  selectedClipIds = new Set<string>();   // full multi-selection

  private undoStack: string[] = [];
  private redoStack: string[] = [];

  constructor(project?: Project) {
    super();
    this.project = project ?? newProject();
    this.selectedTrackId = this.project.tracks[0]?.id ?? null;
  }

  emitChange(scope: ChangeScope = "project") {
    this.project.modifiedAt = Date.now();
    // Every load path (file, browser, remote snapshot) and undo/redo emit "project":
    // generated audio has no stored file, so rebuild any missing buffers first.
    if (scope === "project") this.ensureGenerated();
    this.emit("change", { scope });
  }

  // ---- undo/redo -------------------------------------------------------
  private snapshot(): string {
    return JSON.stringify(this.project);
  }
  pushUndo() {
    this.undoStack.push(this.snapshot());
    if (this.undoStack.length > 100) this.undoStack.shift();
    this.redoStack.length = 0;
  }
  undo() {
    const prev = this.undoStack.pop();
    if (!prev) return;
    this.redoStack.push(this.snapshot());
    this.project = JSON.parse(prev);
    this.emitChange("project");
  }
  redo() {
    const next = this.redoStack.pop();
    if (!next) return;
    this.undoStack.push(this.snapshot());
    this.project = JSON.parse(next);
    this.emitChange("project");
  }

  // ---- selection -------------------------------------------------------
  select(trackId: string | null, clipId: string | null = null) {
    this.selectedTrackId = trackId;
    this.selectedClipId = clipId;
    // Selecting a linked clip visually selects its partner(s) too.
    this.selectedClipIds = clipId ? new Set(this.expandLinked([clipId])) : new Set();
    this.emit("selection", { trackId, clipId });
  }

  isClipSelected(id: string): boolean { return this.selectedClipIds.has(id); }

  /** Toggle a clip in the multi-selection (modifier-click). */
  toggleClipSelection(clipId: string) {
    const found = this.findClip(clipId);
    if (!found) return;
    if (this.selectedClipIds.has(clipId)) {
      this.selectedClipIds.delete(clipId);
      if (this.selectedClipId === clipId) this.selectedClipId = [...this.selectedClipIds].pop() ?? null;
    } else {
      this.selectedClipIds.add(clipId);
      this.selectedClipId = clipId;
      this.selectedTrackId = found.track.id;
    }
    this.emit("selection", { trackId: this.selectedTrackId, clipId: this.selectedClipId });
  }

  /** Replace the multi-selection with the given clip ids (e.g. marquee). */
  setClipSelection(ids: string[]) {
    this.selectedClipIds = new Set(ids);
    this.selectedClipId = ids[ids.length - 1] ?? null;
    const f = this.selectedClipId ? this.findClip(this.selectedClipId) : null;
    if (f) this.selectedTrackId = f.track.id;
    this.emit("selection", { trackId: this.selectedTrackId, clipId: this.selectedClipId });
  }

  /** Resolve the current multi-selection to track/clip pairs. */
  selectedClipList(): { track: Track; clip: Clip }[] {
    const out: { track: Track; clip: Clip }[] = [];
    for (const t of this.project.tracks) for (const c of t.clips) if (this.selectedClipIds.has(c.id)) out.push({ track: t, clip: c });
    return out;
  }

  // ---- lookups ---------------------------------------------------------
  track(id: string): Track | undefined {
    return this.project.tracks.find((t) => t.id === id);
  }
  asset(id: string): AudioAsset | undefined {
    return this.project.assets.find((a) => a.id === id);
  }
  findClip(clipId: string): { track: Track; clip: Clip } | undefined {
    for (const track of this.project.tracks) {
      const clip = track.clips.find((c) => c.id === clipId);
      if (clip) return { track, clip };
    }
    return undefined;
  }

  // ---- assets ----------------------------------------------------------
  addAsset(buffer: AudioBuffer, name: string): AudioAsset {
    const asset: AudioAsset = {
      id: uid("ast"),
      name,
      kind: "audio",
      sampleRate: buffer.sampleRate,
      channels: buffer.numberOfChannels,
      length: buffer.length,
      duration: buffer.duration,
    };
    this.buffers.set(asset.id, buffer);
    this.project.assets.push(asset);
    this.emitChange("assets");
    return asset;
  }

  /** Register a video source as a (silent) video asset. The file's audio, if any,
   *  is imported as a separate audio asset/track so it appears as its own lane —
   *  so video assets never carry an audio buffer (no double playback). */
  addVideoAsset(source: VideoSource, name: string): MediaAsset {
    const id = uid("ast");
    const asset: MediaAsset = {
      id,
      name,
      kind: "video",
      sampleRate: 0,
      channels: 0,
      length: 0,
      duration: source.duration || 0,
      width: source.width,
      height: source.height,
      hasAudio: false,
    };
    this.videos.set(id, source);
    this.project.assets.push(asset);
    this.emitChange("assets");
    return asset;
  }

  /** The video source for an asset, if it is a video. */
  video(assetId: string): VideoSource | undefined { return this.videos.get(assetId); }

  // ---- generated media (color matte, bars & tone, title, countdown) ----------
  /** Add a generated item to the Media bin. Bars get a linked tone track and a
   *  countdown its beeps, as hidden child audio assets (like a video's audio). */
  addGeneratedAsset(spec: VisualSpec, name?: string, defaultDuration?: number): MediaAsset {
    this.pushUndo();
    const d = generatorDurations(spec);
    const asset: MediaAsset = {
      id: uid("ast"), name: name || GENERATOR_LABELS[spec.type], kind: "video",
      sampleRate: 0, channels: 0, length: 0, duration: d.duration,
      width: this.project.width, height: this.project.height, hasAudio: false,
      generator: spec, defaultDuration: Math.min(d.duration, defaultDuration ?? d.defaultDuration), audioAssetIds: [],
    };
    this.project.assets.push(asset);
    this.syncGeneratedAudio(asset);
    this.emitChange("assets");
    return asset;
  }

  /** Change a generated item's settings (every clip using it updates). */
  updateGenerator(assetId: string, spec: VisualSpec, name?: string, defaultDuration?: number) {
    const a = this.asset(assetId);
    if (!a?.generator) return;
    this.pushUndo();
    const oldDuration = a.duration;
    a.generator = spec;
    if (name) a.name = name;
    const d = generatorDurations(spec);
    a.duration = d.duration;
    // A countdown is always as long as its starting number; other items keep
    // the clip length the user chose.
    a.defaultDuration = spec.type === "countdown" ? d.defaultDuration
      : Math.min(d.duration, defaultDuration ?? a.defaultDuration ?? d.defaultDuration);
    for (const t of this.project.tracks) for (const c of t.clips) {
      if (c.assetId !== assetId && !a.audioAssetIds?.includes(c.assetId)) continue;
      const sp = clipSpeed(c);
      // A countdown clip that showed the whole count follows the new count (with
      // its linked beeps); a clip the user trimmed keeps its trim.
      if (spec.type === "countdown" && c.offset < 0.001 && Math.abs(c.duration * sp - oldDuration) < 0.01) {
        c.duration = a.duration / sp;
        continue;
      }
      // never leave a clip reading past the end of shorter media
      c.offset = Math.min(c.offset, Math.max(0, a.duration - 0.04));
      c.duration = Math.min(c.duration, (a.duration - c.offset) / sp);
    }
    this.syncGeneratedAudio(a);
    this.emitChange("assets");
    this.emitChange("tracks");
  }

  /** Create/update/remove the child audio a generated video item carries. */
  private syncGeneratedAudio(a: MediaAsset) {
    const g = a.generator;
    let want: GeneratorSpec | null = null;
    if (g?.type === "bars" && g.tone) want = { type: "tone", freq: g.freq, db: g.db };
    if (g?.type === "countdown" && g.beep) want = { type: "beeps", count: g.from, freq: 1000, db: -12 };
    const childId = a.audioAssetIds?.[0];
    const child = childId ? this.asset(childId) : undefined;
    if (!want) {
      if (child) {
        for (const t of this.project.tracks) t.clips = t.clips.filter((c) => c.assetId !== child.id);
        this.project.assets = this.project.assets.filter((x) => x.id !== child.id);
        this.buffers.delete(child.id);
        // its partner clips lose the link
        for (const t of this.project.tracks) for (const c of t.clips) if (c.assetId === a.id && c.linkId && !this.project.tracks.some((tt) => tt.clips.some((o) => o !== c && o.linkId === c.linkId))) c.linkId = undefined;
      }
      a.audioAssetIds = [];
      a.hasAudio = false;
      return;
    }
    const sr = 48000;
    if (child) {
      child.generator = want;
      child.duration = a.duration; child.defaultDuration = a.defaultDuration;
      child.length = Math.round(a.duration * sr);
      this.buffers.delete(child.id); // rebuilt below
    } else {
      const c: MediaAsset = {
        id: uid("ast"), name: `${a.name} (${want.type === "tone" ? "tone" : "beeps"})`, kind: "audio",
        sampleRate: sr, channels: 1, length: Math.round(a.duration * sr), duration: a.duration,
        ownerId: a.id, generator: want, defaultDuration: a.defaultDuration,
      };
      this.project.assets.push(c);
      a.audioAssetIds = [c.id];
    }
    a.hasAudio = true;
    this.ensureGenerated();
  }

  /** Synthesize the buffer of every generated audio asset that lacks one. */
  ensureGenerated() {
    for (const a of this.project.assets) {
      if (a.kind !== "audio" || !a.generator || this.buffers.has(a.id)) continue;
      const buf = synthAudio(a.generator, a.duration);
      if (buf) this.buffers.set(a.id, buf);
    }
  }

  /** Remove a media asset (and, for a video, its child audio assets) from the
   *  project, along with every clip that references them. */
  removeAsset(assetId: string) {
    const asset = this.asset(assetId);
    if (!asset) return;
    this.pushUndo();
    const ids = new Set<string>([assetId, ...(asset.audioAssetIds ?? [])]);
    for (const t of this.project.tracks) t.clips = t.clips.filter((c) => !ids.has(c.assetId));
    this.project.assets = this.project.assets.filter((a) => !ids.has(a.id));
    for (const id of ids) {
      this.buffers.delete(id);
      const v = this.videos.get(id);
      if (v) { try { v.dispose(); } catch {} this.videos.delete(id); }
    }
    this.selectedClipIds.clear();
    this.selectedClipId = null;
    this.emitChange("project");
  }

  /** Is this asset (or a video's child audio) used by any clip on the timeline? */
  assetInUse(assetId: string): boolean {
    const asset = this.asset(assetId);
    const ids = new Set<string>([assetId, ...(asset?.audioAssetIds ?? [])]);
    return this.project.tracks.some((t) => t.clips.some((c) => ids.has(c.assetId)));
  }

  /** Keep video tracks above audio/instrument tracks (stable within each group).
   *  Video lanes always sit on top; their stacking order (compositing) is preserved. */
  normalizeTrackOrder() {
    const rank = (k: Track["kind"]) => (k === "video" ? 0 : 1);
    this.project.tracks.sort((a, b) => rank(a.kind) - rank(b.kind)); // Array.sort is stable
  }

  // ---- mutations -------------------------------------------------------
  addTrack(track: Track) {
    this.pushUndo();
    this.project.tracks.push(track);
    this.normalizeTrackOrder();
    this.select(track.id, null);
    this.emitChange("tracks");
  }

  /** Trailing number in a track name ("Video 3" → 3); 0 if none. */
  trackNumber(track: Track): number {
    const m = /(\d+)\s*$/.exec(track.name);
    return m ? Number(m[1]) : 0;
  }
  /** A track of the given kind whose name number matches n, if any. */
  findTrackByKindNumber(kind: TrackKind, n: number): Track | undefined {
    return this.project.tracks.find((t) => t.kind === kind && this.trackNumber(t) === n);
  }
  /** Next sequential number for a kind: one past the highest existing of that kind. */
  nextTrackNumber(kind: TrackKind): number {
    const nums = this.project.tracks.filter((t) => t.kind === kind).map((t) => this.trackNumber(t));
    return (nums.length ? Math.max(...nums) : 0) + 1;
  }

  /** Build + insert a track without undo/selection/emit side-effects. */
  private buildTrack(kind: TrackKind, num?: number): Track {
    const n = num ?? this.nextTrackNumber(kind);
    const t = newTrack(this.project.tracks.length, kind);
    t.name = `${TRACK_PREFIX[kind]} ${n}`;
    if (kind === "video") this.project.tracks.unshift(t); // above the highest video
    else this.project.tracks.push(t);                     // below the last audio
    this.normalizeTrackOrder();
    return t;
  }

  /** Create a track. Numbering is per-kind sequential (Video N counts only video
   *  tracks, Audio N only audio). New video tracks go on top of the video block;
   *  audio/instrument tracks go at the bottom. Optional `num` forces the number. */
  createTrack(kind: TrackKind, num?: number): Track {
    this.pushUndo();
    const t = this.buildTrack(kind, num);
    this.select(t.id, null);
    this.emitChange("tracks");
    return t;
  }

  /** The track of `kind` numbered `n`, or a freshly created one (next number). */
  parallelTrack(kind: TrackKind, n: number): Track {
    return this.findTrackByKindNumber(kind, n) ?? this.buildTrack(kind);
  }

  /** Move a clip onto another track without disturbing its link (clears takes). */
  private relocateClip(clip: Clip, dest: Track) {
    for (const t of this.project.tracks) t.clips = t.clips.filter((c) => c !== clip);
    clip.takeGroupId = undefined;
    clip.takeActive = undefined;
    dest.clips.push(clip);
  }

  /** Move a clip to a target track; linked partners follow to the parallel-numbered
   *  track of their own kind (video → Video 3 ⇒ its audio → Audio 3, creating one
   *  with the next number if none exists). Links are preserved. */
  moveLinkedToTrack(clipId: string, targetTrackId: string) {
    const found = this.findClip(clipId);
    const target = this.track(targetTrackId);
    if (!found || !target) return;
    this.pushUndo();
    const partners = this.linkPartners(clipId).map((p) => ({ clip: p.clip, kind: p.track.kind }));
    this.relocateClip(found.clip, target);
    const n = this.trackNumber(target);
    for (const p of partners) {
      const cur = this.findClip(p.clip.id);
      if (!cur || cur.track.id === target.id) continue;
      const dest = this.parallelTrack(p.kind, n);
      if (cur.track.id !== dest.id) this.relocateClip(p.clip, dest);
    }
    this.selectedTrackId = targetTrackId;
    this.emitChange("tracks");
  }
  removeTrack(id: string) {
    this.pushUndo();
    this.project.tracks = this.project.tracks.filter((t) => t.id !== id);
    if (this.selectedTrackId === id) this.select(this.project.tracks[0]?.id ?? null);
    this.emitChange("tracks");
  }
  /** Delete several tracks at once (one undo step) — used by the delete dialog. */
  removeTracks(ids: string[]) {
    const set = new Set(ids);
    if (!set.size) return;
    this.pushUndo();
    this.project.tracks = this.project.tracks.filter((t) => !set.has(t.id));
    if (this.selectedTrackId && set.has(this.selectedTrackId)) this.select(this.project.tracks[0]?.id ?? null);
    this.emitChange("tracks");
  }
  updateTrack(id: string, patch: Partial<Track>, scope: ChangeScope = "mixer") {
    const t = this.track(id);
    if (!t) return;
    Object.assign(t, patch);
    this.emitChange(scope);
  }
  addClip(trackId: string, clip: Clip) {
    this.pushUndo();
    this.track(trackId)?.clips.push(clip);
    this.emitChange("tracks");
  }
  updateClip(clipId: string, patch: Partial<Clip>, takeUndo = true) {
    const found = this.findClip(clipId);
    if (!found) return;
    if (takeUndo) this.pushUndo();
    Object.assign(found.clip, patch);
    this.emitChange("tracks");
  }
  removeClip(clipId: string) {
    const found = this.findClip(clipId);
    if (!found) return;
    this.pushUndo();
    const kill = new Set(this.expandLinked([clipId])); // linked partners go too
    for (const t of this.project.tracks) t.clips = t.clips.filter((c) => !kill.has(c.id));
    for (const id of kill) this.selectedClipIds.delete(id);
    if (this.selectedClipId && kill.has(this.selectedClipId)) this.selectedClipId = null;
    this.emitChange("tracks");
  }

  // ---- link groups (e.g. a video clip + its audio) ---------------------
  /** Clips sharing this clip's linkId (excluding itself). */
  linkPartners(clipId: string): { track: Track; clip: Clip }[] {
    const f = this.findClip(clipId);
    if (!f || !f.clip.linkId) return [];
    const lid = f.clip.linkId;
    const out: { track: Track; clip: Clip }[] = [];
    for (const t of this.project.tracks) for (const c of t.clips) if (c.id !== clipId && c.linkId === lid) out.push({ track: t, clip: c });
    return out;
  }
  /** Expand a set of clip ids to include every link partner. */
  expandLinked(ids: string[]): string[] {
    const set = new Set(ids);
    for (const id of ids) for (const p of this.linkPartners(id)) set.add(p.clip.id);
    return [...set];
  }
  /** Link the given clips into one group (move/trim/split/delete together). */
  linkClips(ids?: string[]) {
    const list = ids && ids.length ? ids : [...this.selectedClipIds];
    if (list.length < 2) return;
    this.pushUndo();
    const lid = uid("lnk");
    for (const id of list) { const f = this.findClip(id); if (f) f.clip.linkId = lid; }
    this.emitChange("tracks");
  }
  /** Unlink every link group touched by the given clips. */
  unlinkClips(ids?: string[]) {
    const list = ids && ids.length ? ids : [...this.selectedClipIds];
    const lids = new Set<string>();
    for (const id of list) { const f = this.findClip(id); if (f?.clip.linkId) lids.add(f.clip.linkId); }
    if (!lids.size) return;
    this.pushUndo();
    for (const t of this.project.tracks) for (const c of t.clips) if (c.linkId && lids.has(c.linkId)) c.linkId = undefined;
    this.emitChange("tracks");
  }

  /** Move a clip to a different track (drag across lanes). Becomes standalone. */
  moveClipToTrack(clipId: string, targetTrackId: string) {
    const found = this.findClip(clipId);
    const target = this.track(targetTrackId);
    if (!found || !target || found.track.id === targetTrackId) return;
    found.track.clips = found.track.clips.filter((c) => c.id !== clipId);
    found.clip.takeGroupId = undefined;
    found.clip.takeActive = undefined;
    target.clips.push(found.clip);
    this.selectedTrackId = targetTrackId;
    this.emitChange("tracks");
  }

  bufferDuration(assetId: string): number {
    return this.buffers.get(assetId)?.duration ?? this.asset(assetId)?.duration ?? Infinity;
  }

  /** Split one clip at absolute timeline `time`, returning the new right-hand clip.
   *  Optional newLinkL/R reassign each side's linkId so link groups stay paired. */
  private splitOne(track: Track, clip: Clip, time: number, newGroupL?: string, newGroupR?: string, newLinkL?: string, newLinkR?: string): Clip | null {
    const end = clip.start + clip.duration;
    if (time <= clip.start + 0.01 || time >= end - 0.01) return null;
    const right: Clip = {
      ...clip,
      id: uid("clip"),
      start: time,
      offset: clip.offset + (time - clip.start) * clipSpeed(clip),
      duration: end - time,
      fadeIn: 0.005,
      takeGroupId: newGroupR,
      transitionIn: undefined, // the tail piece starts at a fresh cut, not the old one
    };
    if (newLinkR !== undefined) right.linkId = newLinkR;
    clip.duration = time - clip.start;
    clip.fadeOut = 0.005;
    clip.takeGroupId = newGroupL;
    if (newLinkL !== undefined) clip.linkId = newLinkL;
    track.clips.push(right);
    return right;
  }

  /** Fresh per-side link ids for a clip's link group, so video+audio stay paired
   *  on each side of a split. Returns [leftLink, rightLink] or [undefined,undefined]. */
  private sideLinks(linkId: string | undefined, cache: Map<string, [string, string]>): [string?, string?] {
    if (!linkId) return [undefined, undefined];
    if (!cache.has(linkId)) cache.set(linkId, [uid("lnk"), uid("lnk")]);
    return cache.get(linkId)!;
  }

  /** Split the clip at `time`. Splits its take group and any linked partners too. */
  splitClipAt(clipId: string, time: number) {
    if (!this.findClip(clipId)) return;
    this.pushUndo();
    const cache = new Map<string, [string, string]>();
    for (const id of this.expandLinked([clipId])) {
      const found = this.findClip(id);
      if (!found) continue;
      const { track, clip } = found;
      const [nl, nr] = this.sideLinks(clip.linkId, cache);
      if (clip.takeGroupId) {
        const groupL = uid("tg"), groupR = uid("tg");
        for (const m of track.clips.filter((c) => c.takeGroupId === clip.takeGroupId)) this.splitOne(track, m, time, groupL, groupR, nl, nr);
      } else {
        this.splitOne(track, clip, time, undefined, undefined, nl, nr);
      }
    }
    this.emitChange("tracks");
  }

  duplicateClip(clipId: string) {
    const found = this.findClip(clipId);
    if (!found) return;
    this.pushUndo();
    const copy: Clip = { ...found.clip, id: uid("clip"), start: found.clip.start + found.clip.duration, takeGroupId: undefined, takeActive: undefined, linkId: undefined, transitionIn: undefined };
    found.track.clips.push(copy);
    this.select(found.track.id, copy.id);
    this.emitChange("tracks");
  }

  // ---- batch (multi-selection) operations: one undo step each -----------
  private idsForBatch(ids?: string[]): string[] {
    return ids && ids.length ? ids : [...this.selectedClipIds];
  }

  removeClips(ids?: string[]) {
    const set = new Set(this.expandLinked(this.idsForBatch(ids))); // linked partners go too
    if (!set.size) return;
    this.pushUndo();
    for (const t of this.project.tracks) t.clips = t.clips.filter((c) => !set.has(c.id));
    this.selectedClipIds.clear();
    this.selectedClipId = null;
    this.emitChange("tracks");
  }

  duplicateClips(ids?: string[]) {
    const list = this.expandLinked(this.idsForBatch(ids));
    if (!list.length) return;
    this.pushUndo();
    const newIds: string[] = [];
    const relink = new Map<string, string>(); // old linkId -> new linkId (keep copies paired)
    for (const id of list) {
      const found = this.findClip(id);
      if (!found) continue;
      let linkId: string | undefined;
      if (found.clip.linkId) { if (!relink.has(found.clip.linkId)) relink.set(found.clip.linkId, uid("lnk")); linkId = relink.get(found.clip.linkId); }
      const copy: Clip = { ...found.clip, id: uid("clip"), start: found.clip.start + found.clip.duration, takeGroupId: undefined, takeActive: undefined, linkId, transitionIn: undefined };
      found.track.clips.push(copy);
      newIds.push(copy.id);
    }
    this.selectedClipIds = new Set(newIds);
    this.selectedClipId = newIds[newIds.length - 1] ?? this.selectedClipId;
    this.emitChange("tracks");
  }

  splitClipsAt(time: number, ids?: string[]) {
    const list = this.expandLinked(this.idsForBatch(ids));
    if (!list.length) return;
    this.pushUndo();
    const cache = new Map<string, [string, string]>();
    for (const id of list) {
      const found = this.findClip(id);
      if (!found) continue;
      const { track, clip } = found;
      const [nl, nr] = this.sideLinks(clip.linkId, cache);
      if (clip.takeGroupId) {
        const groupL = uid("tg"), groupR = uid("tg");
        for (const m of track.clips.filter((c) => c.takeGroupId === clip.takeGroupId)) this.splitOne(track, m, time, groupL, groupR, nl, nr);
      } else {
        this.splitOne(track, clip, time, undefined, undefined, nl, nr);
      }
    }
    this.emitChange("tracks");
  }

  /** Apply a patch to every selected clip (e.g. gain). One undo step. */
  updateSelectedClips(patch: Partial<Clip>, takeUndo = true) {
    const list = this.idsForBatch();
    if (!list.length) return;
    if (takeUndo) this.pushUndo();
    for (const id of list) { const f = this.findClip(id); if (f) Object.assign(f.clip, patch); }
    this.emitChange("tracks");
  }

  /** Trim left/right edge keeping audio aligned to the source. */
  trimClip(clipId: string, edge: "left" | "right", newTime: number) {
    const found = this.findClip(clipId);
    if (!found) return;
    const { clip } = found;
    const oldStart = clip.start, oldDur = clip.duration;
    if (edge === "left") this.trimLeftTo(clip, newTime);
    else this.trimRightTo(clip, newTime);
    // Propagate the same edge delta to linked partners (clamped to each source).
    for (const { clip: p } of this.linkPartners(clipId)) {
      if (edge === "left") this.trimLeftTo(p, p.start + (clip.start - oldStart));
      else this.trimRightTo(p, p.start + p.duration + (clip.duration - oldDur));
    }
    this.emitChange("tracks");
  }

  /** Move a clip's in-point to `t` (timeline s), keeping its media aligned. */
  private trimLeftTo(c: Clip, t: number) {
    const sp = clipSpeed(c), end = c.start + c.duration;
    const ns = Math.max(0, c.start - c.offset / sp, Math.min(t, end - 0.02)); // offset can't go below 0
    c.offset += (ns - c.start) * sp;
    c.duration = end - ns;
    c.start = ns;
  }
  /** Move a clip's out-point to `t`, clamped to the end of its source media. */
  private trimRightTo(c: Clip, t: number) {
    const maxEnd = c.start + (this.bufferDuration(c.assetId) - c.offset) / clipSpeed(c);
    c.duration = Math.min(maxEnd, Math.max(t, c.start + 0.02)) - c.start;
  }

  // ---- NLE editing tools ------------------------------------------------
  /** Clip ids on a track, in timeline order (active takes only). */
  private sortedClips(t: Track): Clip[] {
    return t.clips.filter((c) => c.takeActive !== false).sort((a, b) => a.start - b.start);
  }

  /** Every edit point (clip in/out, markers, In/Out, 0) — for snapping + Up/Down navigation. */
  editPoints(excludeIds?: Set<string>): number[] {
    const pts = new Set<number>([0]);
    for (const t of this.project.tracks) for (const c of t.clips) {
      if (c.takeActive === false || excludeIds?.has(c.id)) continue;
      pts.add(c.start); pts.add(c.start + c.duration);
    }
    for (const m of this.project.markers ?? []) pts.add(m.time);
    if (this.project.markIn != null) pts.add(this.project.markIn);
    if (this.project.markOut != null) pts.add(this.project.markOut);
    return [...pts].sort((a, b) => a - b);
  }

  /** Ripple delete: remove the clips (and link partners) and close the gaps they
   *  leave on their own tracks, so later clips on those tracks slide left. */
  rippleDelete(ids?: string[]) {
    const kill = new Set(this.expandLinked(this.idsForBatch(ids)));
    if (!kill.size) return;
    this.pushUndo();
    for (const t of this.project.tracks) {
      const gone = t.clips.filter((c) => kill.has(c.id)).map((c) => [c.start, c.start + c.duration] as [number, number]);
      if (!gone.length) continue;
      t.clips = t.clips.filter((c) => !kill.has(c.id));
      const merged = mergeIntervals(gone);
      for (const c of t.clips) {
        let shift = 0;
        for (const [s, e] of merged) if (e <= c.start + EPS) shift += e - s;
        c.start = Math.max(0, c.start - shift);
      }
    }
    this.selectedClipIds.clear();
    this.selectedClipId = null;
    this.emitChange("tracks");
  }

  /** Close the empty gap containing `time` on a track: clips after it (and their
   *  link partners, so A/V stays in sync) slide left to the previous clip's end. */
  closeGapAt(trackId: string, time: number): boolean {
    const t = this.track(trackId);
    if (!t) return false;
    const clips = this.sortedClips(t);
    if (clips.some((c) => time >= c.start && time < c.start + c.duration)) return false; // not a gap
    const prevEnd = clips.filter((c) => c.start + c.duration <= time + EPS).reduce((m, c) => Math.max(m, c.start + c.duration), 0);
    const after = clips.filter((c) => c.start >= time - EPS);
    if (!after.length) return false;
    const gap = after[0].start - prevEnd;
    if (gap <= EPS) return false;
    this.pushUndo();
    const move = new Set(this.expandLinked(after.map((c) => c.id)));
    for (const tr of this.project.tracks) for (const c of tr.clips) if (move.has(c.id)) c.start = Math.max(0, c.start - gap);
    this.emitChange("tracks");
    return true;
  }

  /** Remove everything between `start` and `end` on every track (splitting clips
   *  that straddle the edges). Lift leaves a gap; Extract (ripple) closes it. */
  removeRange(start: number, end: number, ripple: boolean) {
    if (end - start <= EPS) return;
    this.pushUndo();
    const relink = new Map<string, [string, string]>(); // linkId -> [left piece, right piece]
    const side = (lid: string | undefined, i: 0 | 1) => {
      if (!lid) return undefined;
      if (!relink.has(lid)) relink.set(lid, [uid("lnk"), uid("lnk")]);
      return relink.get(lid)![i];
    };
    const len = end - start;
    for (const t of this.project.tracks) {
      const out: Clip[] = [];
      for (const c of t.clips) {
        const cEnd = c.start + c.duration;
        if (cEnd <= start + EPS || c.start >= end - EPS) {
          if (ripple && c.start >= end - EPS) c.start -= len;
          out.push(c); continue;
        }
        const sp = clipSpeed(c);
        if (c.start < start - EPS) out.push({ ...c, id: uid("clip"), duration: start - c.start, linkId: side(c.linkId, 0) });
        if (cEnd > end + EPS) {
          out.push({ ...c, id: uid("clip"), start: ripple ? start : end, offset: c.offset + (end - c.start) * sp, duration: cEnd - end, linkId: side(c.linkId, 1), transitionIn: undefined });
        }
      }
      t.clips = out;
    }
    if (ripple) for (const m of this.project.markers ?? []) if (m.time >= end) m.time -= len;
    this.selectedClipIds.clear();
    this.selectedClipId = null;
    this.emitChange("tracks");
  }

  /** Trim the clips under the playhead on the given tracks (all tracks if none):
   *  "start" cuts the head up to `time`, "end" cuts the tail from `time`. With
   *  ripple, the head trim pulls the rest of the track left to fill the hole. */
  trimToTime(time: number, edge: "start" | "end", ripple: boolean, ids?: string[]) {
    const pick = (ids && ids.length ? this.expandLinked(ids) : null);
    const hits: { track: Track; clip: Clip }[] = [];
    for (const t of this.project.tracks) for (const c of t.clips) {
      if (c.takeActive === false) continue;
      if (pick && !pick.includes(c.id)) continue;
      if (time > c.start + EPS && time < c.start + c.duration - EPS) hits.push({ track: t, clip: c });
    }
    if (!hits.length) return false;
    this.pushUndo();
    for (const { track, clip } of hits) {
      if (edge === "end") {
        const cut = clip.start + clip.duration - time;
        clip.duration = time - clip.start;
        if (ripple) for (const c of track.clips) if (c !== clip && c.start >= time + cut - EPS) c.start -= cut;
      } else {
        const cut = time - clip.start;
        clip.offset += cut * clipSpeed(clip);
        clip.duration -= cut;
        if (ripple) { for (const c of track.clips) if (c !== clip && c.start >= time - EPS) c.start -= cut; }
        else clip.start = time;
      }
    }
    this.emitChange("tracks");
    return true;
  }

  /** Shift clips (and link partners) in time by `delta` seconds (nudge). */
  nudgeClips(delta: number, ids?: string[]) {
    const list = this.expandLinked(this.idsForBatch(ids));
    if (!list.length) return;
    // Don't let any clip cross 0 — clamp the whole group's delta instead.
    let d = delta;
    for (const id of list) { const f = this.findClip(id); if (f) d = Math.max(d, -f.clip.start); }
    if (Math.abs(d) < EPS) return;
    this.pushUndo();
    for (const id of list) { const f = this.findClip(id); if (f) f.clip.start += d; }
    this.emitChange("tracks");
  }

  /** Allowed slip range (source seconds) for a clip, so its offset stays inside the media. */
  slipRange(c: Clip): [number, number] {
    const maxOff = Math.max(0, this.bufferDuration(c.assetId) - c.duration * clipSpeed(c));
    return [-c.offset, maxOff - c.offset];
  }

  /** Change playback speed. The clip keeps its start and source range, so its
   *  timeline duration scales; link partners change with it to stay in sync. */
  setClipSpeed(clipId: string, speed: number) {
    const s = Math.max(0.1, Math.min(16, speed));
    const ids = this.expandLinked([clipId]);
    if (!ids.length) return;
    this.pushUndo();
    for (const id of ids) {
      const f = this.findClip(id);
      if (!f) continue;
      const span = f.clip.duration * clipSpeed(f.clip);
      f.clip.speed = Math.abs(s - 1) < 1e-6 ? undefined : s;
      f.clip.duration = span / s;
      f.clip.fadeIn = Math.min(f.clip.fadeIn, f.clip.duration / 2);
      f.clip.fadeOut = Math.min(f.clip.fadeOut, f.clip.duration / 2);
    }
    this.emitChange("tracks");
  }

  /** Enable/disable the selected clips (disabled clips are skipped in playback/export). */
  toggleClipsEnabled(ids?: string[]) {
    const list = this.expandLinked(this.idsForBatch(ids));
    if (!list.length) return;
    const clips = list.map((id) => this.findClip(id)?.clip).filter(Boolean) as Clip[];
    const disable = clips.some((c) => !c.disabled); // any enabled → disable all
    this.pushUndo();
    for (const c of clips) c.disabled = disable || undefined;
    this.emitChange("tracks");
  }

  /** Select every clip that starts at or after `time` (optionally on one track). */
  selectForward(time: number, trackId?: string) {
    const ids: string[] = [];
    for (const t of this.project.tracks) {
      if (trackId && t.id !== trackId) continue;
      for (const c of t.clips) if (c.takeActive !== false && c.start >= time - EPS) ids.push(c.id);
    }
    this.setClipSelection(ids);
  }

  // ---- clipboard ----------------------------------------------------------
  private clipboard: ClipboardItem[] = [];
  hasClipboard() { return this.clipboard.length > 0; }

  /** Copy the selection (with link partners) to the internal clipboard. */
  copyClips(ids?: string[]): number {
    const list = this.expandLinked(this.idsForBatch(ids));
    const items = list.map((id) => this.findClip(id)).filter(Boolean) as { track: Track; clip: Clip }[];
    if (!items.length) return 0;
    const t0 = Math.min(...items.map((i) => i.clip.start));
    this.clipboard = items.map(({ track, clip }) => ({ trackId: track.id, trackKind: track.kind, rel: clip.start - t0, clip: JSON.parse(JSON.stringify(clip)) }));
    return items.length;
  }

  /** Paste the clipboard at `time` onto the tracks it came from (or the first
   *  track of the same kind). Copies stay linked to each other, not the originals. */
  pasteClips(time: number): number {
    if (!this.clipboard.length) return 0;
    this.pushUndo();
    const relink = new Map<string, string>();
    const newIds: string[] = [];
    for (const it of this.clipboard) {
      const track = this.track(it.trackId) ?? this.project.tracks.find((t) => t.kind === it.trackKind) ?? this.buildTrack(it.trackKind);
      let linkId: string | undefined;
      if (it.clip.linkId) { if (!relink.has(it.clip.linkId)) relink.set(it.clip.linkId, uid("lnk")); linkId = relink.get(it.clip.linkId); }
      const copy: Clip = { ...JSON.parse(JSON.stringify(it.clip)), id: uid("clip"), start: Math.max(0, time + it.rel), takeGroupId: undefined, takeActive: undefined, linkId };
      track.clips.push(copy);
      newIds.push(copy.id);
    }
    // A pasted transition only survives if the clip it dissolves from was pasted too.
    const pasted = new Set(newIds);
    for (const id of newIds) {
      const f = this.findClip(id);
      if (!f?.clip.transitionIn) continue;
      const prev = clipBefore(f.track.clips, f.clip);
      if (!prev || !pasted.has(prev.id)) f.clip.transitionIn = undefined;
    }
    this.selectedClipIds = new Set(newIds);
    this.selectedClipId = newIds[newIds.length - 1] ?? null;
    this.emitChange("tracks");
    this.emit("selection", { trackId: this.selectedTrackId, clipId: this.selectedClipId });
    return newIds.length;
  }

  // ---- transitions (cross-dissolve / crossfade) ---------------------------
  /** The clip abutting this one before / after it on its track. */
  neighbor(clipId: string, side: "before" | "after"): Clip | undefined {
    const f = this.findClip(clipId);
    if (!f) return undefined;
    return side === "before" ? clipBefore(f.track.clips, f.clip) : clipAfter(f.track.clips, f.clip);
  }

  /** Set (or with 0/undefined, remove) the transition at the head of `clipId`.
   *  Link partners cut at the same moment get the same transition, so a video
   *  dissolve brings its audio crossfade along. Returns how many cuts changed. */
  setTransition(clipId: string, duration: number | undefined, takeUndo = true): number {
    const f = this.findClip(clipId);
    if (!f) return 0;
    const cut = f.clip.start;
    const targets = this.expandLinked([clipId])
      .map((id) => this.findClip(id))
      .filter((x): x is { track: Track; clip: Clip } => !!x && Math.abs(x.clip.start - cut) < 0.01 && !!clipBefore(x.track.clips, x.clip));
    if (!targets.length) return 0;
    if (takeUndo) this.pushUndo();
    const d = duration && duration > 0 ? duration : undefined;
    for (const t of targets) t.clip.transitionIn = d;
    this.emitChange("tracks");
    return targets.length;
  }

  /** Add the default transition at the cut nearest `time` (within `maxDist`) on
   *  the given tracks (all tracks if none). Returns the cut time used, or null. */
  addTransitionNear(time: number, trackIds?: string[], duration = DEFAULT_TRANSITION, maxDist = 1): number | null {
    let best: { clip: Clip; dist: number } | null = null;
    for (const t of this.project.tracks) {
      if (trackIds && !trackIds.includes(t.id)) continue;
      for (const c of t.clips) {
        const dist = Math.abs(c.start - time);
        if (dist > maxDist || (best && dist >= best.dist) || !clipBefore(t.clips, c)) continue;
        best = { clip: c, dist };
      }
    }
    if (!best) return null;
    const cut = best.clip.start;
    // Apply at that cut on every (targeted) track that has one there, in one undo step.
    this.pushUndo();
    for (const t of this.project.tracks) {
      if (trackIds && !trackIds.includes(t.id)) continue;
      for (const c of t.clips) if (Math.abs(c.start - cut) < 0.01 && clipBefore(t.clips, c)) this.setTransition(c.id, duration, false);
    }
    return cut;
  }

  // ---- markers + In/Out ---------------------------------------------------
  addMarker(time: number, name?: string): Marker {
    this.pushUndo();
    const list = (this.project.markers ??= []);
    const m: Marker = { id: uid("mk"), time: Math.max(0, time), name: name ?? `Marker ${list.length + 1}` };
    list.push(m);
    list.sort((a, b) => a.time - b.time);
    this.emitChange("tracks");
    return m;
  }
  updateMarker(id: string, patch: Partial<Marker>) {
    const m = this.project.markers?.find((x) => x.id === id);
    if (!m) return;
    this.pushUndo();
    Object.assign(m, patch);
    this.project.markers!.sort((a, b) => a.time - b.time);
    this.emitChange("tracks");
  }
  removeMarker(id: string) {
    if (!this.project.markers?.some((m) => m.id === id)) return;
    this.pushUndo();
    this.project.markers = this.project.markers.filter((m) => m.id !== id);
    this.emitChange("tracks");
  }
  setMark(which: "in" | "out", time: number | undefined) {
    if (which === "in") this.project.markIn = time;
    else this.project.markOut = time;
    const { markIn, markOut } = this.project;
    if (markIn != null && markOut != null && markOut < markIn) { this.project.markIn = markOut; this.project.markOut = markIn; }
    this.emitChange("tracks");
  }

  /** Promote a take to active within its group (others muted). */
  setActiveTake(clipId: string) {
    const found = this.findClip(clipId);
    if (!found || !found.clip.takeGroupId) return;
    this.pushUndo();
    for (const c of found.track.clips)
      if (c.takeGroupId === found.clip.takeGroupId) c.takeActive = c.id === clipId;
    this.select(found.track.id, clipId);
    this.emitChange("tracks");
  }

  /** Clear a time range on a track, trimming/splitting any clips that overlap it
   *  (used by Overwrite record mode so the new take punches over old audio). */
  overwriteRegion(trackId: string, start: number, end: number, exceptClipId?: string) {
    const track = this.track(trackId);
    if (!track || end <= start) return;
    const result: Clip[] = [];
    for (const c of track.clips) {
      const cEnd = c.start + c.duration;
      if (c.id === exceptClipId || cEnd <= start || c.start >= end) { result.push(c); continue; }
      if (c.start < start) { // keep the part before the region
        result.push({ ...c, id: uid("clip"), duration: start - c.start, fadeOut: 0.005, takeGroupId: undefined, takeActive: undefined });
      }
      if (cEnd > end) { // keep the part after the region
        result.push({ ...c, id: uid("clip"), start: end, offset: c.offset + (end - c.start) * clipSpeed(c), duration: cEnd - end, fadeIn: 0.005, takeGroupId: undefined, takeActive: undefined, transitionIn: undefined });
      }
      // the overlapped middle is dropped
    }
    track.clips = result;
    this.emitChange("tracks");
  }

  /** Group a freshly recorded clip with any clips it overlaps into a take stack. */
  groupAsTake(trackId: string, newClipId: string) {
    const track = this.track(trackId);
    const nc = track?.clips.find((c) => c.id === newClipId);
    if (!track || !nc) return;
    const s = nc.start, e = nc.start + nc.duration;
    const overlapping = track.clips.filter((c) => c.id !== newClipId && c.start < e && c.start + c.duration > s);
    if (overlapping.length === 0) return;
    const groupId = overlapping.find((c) => c.takeGroupId)?.takeGroupId ?? uid("tg");
    for (const c of overlapping) { c.takeGroupId = groupId; c.takeActive = false; }
    // also fold in any prior members of that group not currently overlapping
    for (const c of track.clips) if (c.takeGroupId === groupId) c.takeActive = false;
    nc.takeGroupId = groupId;
    nc.takeActive = true;
    track.compExpanded = true;
    this.emitChange("tracks");
  }

  toggleCompExpand(trackId: string) {
    const t = this.track(trackId);
    if (!t) return;
    t.compExpanded = !t.compExpanded;
    this.emitChange("tracks");
  }

  /** Active takes only (used by engine/render). A clip is active unless it is an
   *  inactive member of a take group. */
  activeClips(track: Track): Clip[] {
    return track.clips.filter((c) => c.takeActive !== false);
  }

  // ---- effects ---------------------------------------------------------
  addEffect(trackId: string | "master", fx: Effect) {
    this.pushUndo();
    if (trackId === "master") this.project.masterFx.push(fx);
    else this.track(trackId)?.fx.push(fx);
    this.emitChange("mixer");
  }
  removeEffect(trackId: string | "master", fxId: string) {
    this.pushUndo();
    const list = trackId === "master" ? this.project.masterFx : this.track(trackId)?.fx;
    if (!list) return;
    const i = list.findIndex((f) => f.id === fxId);
    if (i >= 0) list.splice(i, 1);
    this.emitChange("mixer");
  }
  updateEffect(trackId: string | "master", fxId: string, patch: Partial<Effect>) {
    const list = trackId === "master" ? this.project.masterFx : this.track(trackId)?.fx;
    const fx = list?.find((f) => f.id === fxId);
    if (!fx) return;
    // Merge params (a partial {params:{key:v}} must not drop the other params).
    const { params, ...rest } = patch;
    Object.assign(fx, rest);
    if (params) Object.assign(fx.params, params);
    this.emitChange("mixer");
  }

  /** Save a user effect preset into the project (kind-scoped). */
  saveFxPreset(preset: import("./model").FxPreset) {
    this.pushUndo();
    (this.project.fxPresets ??= []).push(preset);
    this.emitChange("mixer");
  }
  deleteFxPreset(id: string) {
    if (!this.project.fxPresets) return;
    this.pushUndo();
    this.project.fxPresets = this.project.fxPresets.filter((p) => p.id !== id);
    this.emitChange("mixer");
  }

  /** Total content length in seconds (for timeline sizing). */
  contentLength(): number {
    return Math.max(16, this.contentEnd());
  }

  /** End of the last active clip in seconds (0 for an empty project). */
  contentEnd(): number {
    let max = 0;
    for (const t of this.project.tracks)
      for (const c of t.clips) if (c.takeActive !== false) max = Math.max(max, c.start + c.duration);
    return max;
  }

  /** What the loop button repeats: the In->Out range when both marks are set
   *  (the editor convention), else a legacy loop region, else the whole program. */
  loopRange(): { start: number; end: number } | null {
    const p = this.project;
    if (p.markIn != null && p.markOut != null && p.markOut > p.markIn) return { start: p.markIn, end: p.markOut };
    if (p.loopEnd > p.loopStart) return { start: p.loopStart, end: p.loopEnd };
    const end = this.contentEnd();
    return end > 0 ? { start: 0, end } : null;
  }

  /** Are any tracks soloed? */
  anySolo(): boolean {
    return this.project.tracks.some((t) => t.solo);
  }
}

/** Merge overlapping [start,end] intervals (sorted output). */
function mergeIntervals(list: [number, number][]): [number, number][] {
  const s = [...list].sort((a, b) => a[0] - b[0]);
  const out: [number, number][] = [];
  for (const iv of s) {
    const last = out[out.length - 1];
    if (last && iv[0] <= last[1] + EPS) last[1] = Math.max(last[1], iv[1]);
    else out.push([...iv] as [number, number]);
  }
  return out;
}
